import { Project } from '@awork/features/project/models/project.model'
import { Task } from '@awork/features/task/models/task.model'
import { TimeTracking } from '@awork/features/time-tracking/models/time-tracking.model'
import { Company } from '@awork/features/company/models/company.model'
import { User } from '@awork/features/user/models/user.model'
import type { TimeTrackingStore } from '@awork/features/time-tracking/state/time-tracking.store'
import type { ProjectStore } from '@awork/features/project/state/project.store'
import type { ProjectStatusStore } from '@awork/features/project/state/project-status.store'
import type { TaskStore } from '@awork/features/task/state/task.store'
import type { TaskStatusStore } from '@awork/features/task/state/task-status.store'
import type { TaskListStore } from '@awork/features/task/state/task-list.store'
import type { ChecklistItemStore } from '@awork/features/task/state/checklist-item.store'
import type { CompanyStore } from '@awork/features/company/state/company.store'
import type { UserStore } from '@awork/features/user/state/user.store'
import { Comment } from '@awork/_shared/models/comment.model'
import { TaskList } from '@awork/features/task/models/task-list.model'
import { TaskStatus } from '@awork/features/task/models/task-status.model'
import type { NotificationStore } from '@awork/framework/state/notification.store'
import { Notification } from '@awork/framework/models/notification.model'
import { ChecklistItem } from '@awork/features/task/models/checklist-item'
import type { TaskDependencySignal } from '@awork/features/task/models/task-dependency.model'
import type { Absence } from '@awork/features/user/models/absence.model'
import type { IWatchEntity } from '@awork/framework/services/notification-service/types'
import { Retainer } from '@awork/features/project/models/retainer.model'
import { RetainerBudget } from '@awork/features/project/models/retainer-budget.model'
import type { TimeBookingStore } from '@awork/features/planner/state/time-booking.store'
import { TimeBooking } from '@awork/features/planner/models/time-booking.model'
import type { DocumentStore } from '@awork/features/document/state/document.store'
import type { DocumentSpaceStore } from '@awork/features/document/state/document-space.store'
import type { Document } from '@awork/features/document/models/document.model'
import type { DocumentSpace } from '@awork/features/document/models/document-space.model'
import { WorkType } from '@awork/features/task/models/work-type.model'
import type { WorkTypeStore } from '@awork/features/task/state/work-type.store'
import type { TaskSchedule } from '@awork/features/planner/models/task-schedule.model'
import type { TaskScheduleStore } from '@awork/features/planner/state/task-schedule.store'
import type { CommentStore } from '@awork/_shared/state/comment.store'
import { Workflow } from '@awork/features/workflow/models/workflow.model'
import type { WorkflowStore } from '@awork/features/workflow/state/workflow.store'
import { ProjectStatus } from '@awork/features/project/models/project-status.model'
import { FilterEntity } from '@awork/_shared/models/filters.model'
import type { EntityFilterStore } from '@awork/_shared/state/entity-filter.store'
import { AgentThread } from '@awork/features/agent/models/agent-thread.model'
import { Agent } from '@awork/features/agent/models/agent.model'
import { SystemPromptVersion } from '@awork/features/agent/models/system-prompt-version.model'
import { AgentPrompt } from '@awork/features/agent/models/agent-prompt.model'
import { AgentSkill } from '@awork/features/agent/models/agent-skill.model'
import type { AgentConnector, CustomAgentConfiguration } from '@awork/features/agent/services/agent-service/types'
import type { AgentStore } from '@awork/features/agent/state/agent.store'
import type { AgentSkillStore } from '@awork/features/agent/state/agent-skill.store'
import type { AgentConnectorStore } from '@awork/features/agent/state/agent-connector.store'

export type WebSocketSignalEntityType =
  | 'project'
  | 'task'
  | 'timetracking'
  | 'company'
  | 'user'
  | 'comment'
  | 'watch'
  | 'tasklist'
  | 'taskstatus'
  | 'notification'
  | 'checklistitem'
  | 'taskdependency'
  | 'absence'
  | 'retainer'
  | 'retainerbudget'
  | 'projecttimebooking'
  | 'document'
  | 'documentspace'
  | 'typeofwork'
  | 'taskschedule'
  | 'workflow'
  | 'projectstatus'
  | 'workflowtocustomfielddefinitions'
  | 'projectcopycompletion'
  | 'filter'
  | 'thread'
  | 'widget'
  | 'agent'
  | 'systempromptversion'
  | 'agentprompt'
  | 'agentskill'
  | 'agentconnector'
  | 'agentskilllink'
  | 'personalagentskilllink'
  | 'agentconnectorlink'
  | 'reportdashboard'

export interface DashboardWidgetSignal {
  id: string
  dashboardId: string
}

export interface IWorkflowScopedEntity {
  workflowId?: string
  linkedProjectIds?: string[]
}

export interface IWorkflowCustomFieldDefinitionLinkEntity extends IWorkflowScopedEntity {
  id: string
  customFieldDefinitionId: string
  order?: number
  customFieldDefinition?: {
    id: string
    name: string
    type: string
    entity: string
    selectionOptions?: { id: string; value: string; color?: string; order: number }[]
  }
}

export type WebSocketSignalEntity =
  | Project
  | Task
  | TimeTracking
  | Company
  | User
  | Comment
  | IWatchEntity
  | TaskList
  | TaskStatus
  | Notification
  | ChecklistItem
  | TaskDependencySignal
  | Absence
  | Retainer
  | RetainerBudget
  | TimeBooking
  | Document
  | DocumentSpace
  | WorkType
  | TaskSchedule
  | Workflow
  | ProjectStatus
  | IWorkflowCustomFieldDefinitionLinkEntity
  | ProjectCopyCompletionSignal
  | FilterEntity
  | AgentThread
  | DashboardWidgetSignal
  | Agent
  | SystemPromptVersion
  | AgentPrompt
  | AgentSkill
  | AgentConnector
  | AgentSkillLinkSignal
  | PersonalAgentSkillLinkSignal
  | AgentConnectorLinkSignal

export type WebSocketSignalEntityStore =
  | ProjectStore
  | TaskStore
  | TimeTrackingStore
  | CompanyStore
  | UserStore
  | TaskListStore
  | TaskStatusStore
  | ProjectStatusStore
  | NotificationStore
  | ChecklistItemStore
  | TimeBookingStore
  | DocumentStore
  | DocumentSpaceStore
  | WorkTypeStore
  | TaskScheduleStore
  | CommentStore
  | WorkflowStore
  | EntityFilterStore
  | AgentStore
  | AgentSkillStore
  | AgentConnectorStore

export interface AgentSkillLinkSignal {
  id: string
  agentId: string
  skillId: string
}

export interface PersonalAgentSkillLinkSignal {
  id: string
  skillId: string
}

export interface AgentConnectorLinkSignal {
  id: string
  agentId: string
  connectorId: string
  connector?: AgentConnector
  configuration?: CustomAgentConfiguration
}

export type WebSocketSignalOperation = 'added' | 'updated' | 'deleted'

export interface ExcludedSignal {
  entityName: WebSocketSignalEntityType
  propertyName: string
  entityId?: string
  expiresAt?: number
}

interface WebSocketSignalEntityChange {
  property: string
  old: string | null
  new: string | null
}

export interface ProjectCopyCompletionSignal {
  userId: string
  operationId: string
  projectId: string
  succeeded: boolean
  warnings: string[]
  warningDetails?: ProjectCopyWarningDetailSignal[]
}

export interface ProjectCopyWarningDetailSignal {
  type: string
  message: string
  details: Record<string, string>
}

export interface IWebSocketSignal {
  entityName: WebSocketSignalEntityType
  entityId: string
  entity: WebSocketSignalEntity
  operationType: WebSocketSignalOperation
  createdBy: string
  traceId?: string
  changes: WebSocketSignalEntityChange[]
}

export class WebSocketSignal implements IWebSocketSignal {
  entityName: WebSocketSignalEntityType
  entityId: string
  entity: WebSocketSignalEntity
  operationType: WebSocketSignalOperation
  createdBy: string
  traceId?: string
  changes: WebSocketSignalEntityChange[]

  constructor(signal: IWebSocketSignal, currentUser: User) {
    Object.assign(this, signal)

    if (this.entity) {
      switch (this.entityName) {
        case 'project':
          this.entity = new Project(this.entity as Project)
          break
        case 'task':
          this.entity = new Task(this.entity as Task)
          break
        case 'user':
          this.entity = new User(this.entity as User, 'Signal')
          break
        case 'company':
          this.entity = new Company(this.entity as Company)
          break
        case 'timetracking':
          this.entity = new TimeTracking(this.entity as TimeTracking)
          break
        case 'comment':
          this.entity = new Comment(this.entity as Comment)
          break
        case 'agent':
          this.entity = new Agent(this.entity as Agent)
          break
        case 'systempromptversion':
          this.entity = new SystemPromptVersion(this.entity as SystemPromptVersion)
          break
        case 'agentprompt':
          this.entity = new AgentPrompt(this.entity as AgentPrompt)
          break
        case 'agentskill':
          this.entity = new AgentSkill(this.entity as AgentSkill)
          break
        case 'watch':
          this.entity = this.entity as IWatchEntity
          break
        case 'tasklist':
          this.entity = new TaskList(this.entity as TaskList)
          break
        case 'taskstatus':
          this.entity = new TaskStatus(this.entity as TaskStatus)
          break
        case 'projectstatus':
          this.entity = new ProjectStatus(this.entity as ProjectStatus)
          break
        case 'checklistitem':
          this.entity = new ChecklistItem(this.entity as ChecklistItem)
          break
        case 'notification':
          this.entity = new Notification(this.entity as Notification, currentUser)
          break
        case 'retainer':
          this.entity = new Retainer(this.entity as Retainer)
          break
        case 'retainerbudget':
          this.entity = new RetainerBudget(this.entity as RetainerBudget)
          break
        case 'projecttimebooking':
          this.entity = new TimeBooking(this.entity as TimeBooking)
          break
        case 'typeofwork':
          this.entity = new WorkType(this.entity as WorkType)
          break
        case 'workflow':
          this.entity = new Workflow(this.entity as Workflow)
          break
        case 'workflowtocustomfielddefinitions':
          this.entity = this.entity as IWorkflowCustomFieldDefinitionLinkEntity
          break
        case 'filter':
          this.entity = new FilterEntity(this.entity as FilterEntity)
          break
        case 'thread':
          this.entity = new AgentThread(this.entity as AgentThread)
          break
      }
    }
  }
}
